import useRequestFormStore from './store';
import { usePostCaseConfigs } from './hooks/PostCaseUtilities';
import shallow from 'zustand/shallow';
import { useEffect, useState } from 'react';
import { CancerConfig } from './configs/cancerConfig';
import { DiseaseDto } from './dtos/Event';
import { nanoid } from 'nanoid';
import { useQueryParameters } from '@/hooks/useQueryParameters';
import { eventKeys, useDiseaseForEvent } from './hooks/useEvent';
import { CaseFormOptionsDto } from './hooks/useCaseFormOptions';
import { useQueryClient } from 'react-query';
import Skeleton from '@oncolens/oncolens-ui/lib/components/Common/Skeleton';
import TabbedContainer from '@oncolens/oncolens-ui/lib/components/FormControls/TabbedContainer';
import DiseaseItem from './components/DiseaseItem';

function getTabLabel(cancerLocationId: string | undefined, config: CancerConfig[] | undefined) {
  if (!cancerLocationId || !config) return '';

  const cancerType = config.find((x) => x.locations.some((y) => y.value === cancerLocationId));

  return cancerType
    ? `${cancerType.label} - ${
      cancerType.locations.find((x) => x.value === cancerLocationId)?.label
		  }`
    : '';
}

function getTabSubtitle(
  cancerLocationId: string | undefined,
  cancerPathologyId: string | undefined,
  config: CancerConfig[] | undefined
) {
  if (!cancerLocationId || !cancerPathologyId || !config) return '';

  const cancerType = config.find((x) => x.locations.some((y) => y.value === cancerLocationId));

  return cancerType
    ? cancerType.pathologies.find((pathology) => pathology.value === cancerPathologyId)?.label
    : '';
}

function mapDiseases(data: DiseaseDto[] | undefined) {
  return (
    data
      ?.sort((a, b) => a.order - b.order)
      .reduce(
        (diseaseAccum, diseaseCurr) => ({
          ...diseaseAccum,
          [diseaseCurr.id]: {
            ...diseaseCurr,
            isSaved: true,
            episodes: diseaseCurr.episodes.reduce(
              (epAccum, epCurr) => ({
                ...epAccum,
                [epCurr.id]: {
                  ...epCurr,
                  isSaved: true
                }
              }),
              {}
            )
          }
        }),
        {}
      ) ?? {}
  );
}

function mapPatientHistory(data: DiseaseDto[] | undefined, formOptions: CaseFormOptionsDto) {
  const primaryDisease = data?.find((x) => x.isPrimary) as DiseaseDto;

  return {
    chiefComplaint: primaryDisease.chiefComplaint || formOptions.chiefComplaintTemplate,
    medicalHistory: primaryDisease.medicalHistory || formOptions.medicalHistoryTemplate,
    familyHistory: primaryDisease.familyHistory || formOptions.familyHistoryTemplate,
    socialHistory: primaryDisease.socialHistory,
    menopausalStatus: primaryDisease.menopausalStatus,
    breastOvarianCancerNear:
			primaryDisease.breastOvarianCancerNear === true
			  ? 1
			  : primaryDisease.breastOvarianCancerNear === false
			    ? 0
			    : null,
    miscellaneousCaseDetails:
			primaryDisease.miscellaneousCaseDetails || formOptions.miscCaseDetailsTemplate
  };
}

export default function DiseaseForm() {
  const configs = usePostCaseConfigs(['cancer']);
  const [diseases, updateStore, updateField, formOptions] = useRequestFormStore(
    (store) => [
      store.diseases,
      store.updateStore,
      store.updateField,
      store.formOptions,
      store.patientHistory
    ],
    shallow
  );
  const [activeId, setActiveId] = useState('');
  const tabs = Object.keys(diseases ?? {})?.map((key) => ({
    id: key,
    isPrimary: diseases[key].isPrimary,
    title:
			getTabLabel(diseases[key].cancerLocationId, configs?.cancer) ||
			(diseases[key].isPrimary ? 'Primary Disease' : 'Additional Disease'),
    subtitle: getTabSubtitle(
      diseases[key].cancerLocationId,
      diseases[key].cancerPathologyId,
      configs?.cancer
    )
  }));

  const onAddDisease = () => {
    const newDiseaseId = nanoid();

    updateStore((store) => {
      store.diseases[newDiseaseId] = {
        isPrimary: false,
        isSaved: false,
        cancerLocationId: null,
        episodes: {
          [nanoid()]: {
            isTargetEpisode: true,
            diagnosisStatus: null,
            laterality: null,
            isSaved: false,
            areasOfRecurrence: [],
            areasOfMetastasis: []
          }
        }
      };

      store.dirtyFields.push(`diseases.${newDiseaseId}`);
    });

    setActiveId(newDiseaseId);
  };

  const parameters = useQueryParameters();
  const eventId = parameters.get('eventId') || '';

  const { isLoading } = useDiseaseForEvent(eventId, {
    enabled: !!eventId,
    onSuccess: (data) => {
      const mappedDiseases = mapDiseases(data);
      updateField('diseases', mappedDiseases, { markDirty: false });

      const mappedPatientHistory = mapPatientHistory(data, formOptions);
      updateField('patientHistory', mappedPatientHistory, { markDirty: false });
      if (!activeId) {
        setActiveId((data.find((x) => x.isPrimary) as DiseaseDto).id);
      }
    },
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    const keys = Object.keys(diseases ?? {});
    const isActiveIdValid = keys.some((key) => key === activeId);

    // there is a case when an added disease is saved for the first time
    // hence the id of the disease is different to the temporary id when the tab
    // was added
    if (!isActiveIdValid) {
      if (activeId) {
        const newTabActiveKey = keys.find((key) => activeId === diseases[key].tempId);
        if (newTabActiveKey) {
          setActiveId(newTabActiveKey);
          return;
        }
      }
      setActiveId(keys.find((key) => diseases[key].isPrimary) as string);
    }
  }, [diseases, activeId]);

  const client = useQueryClient();

  // Invalidate query on mount so we can re-hydrate with the latest data on load
  useEffect(() => {
    if (eventId) {
      client.invalidateQueries(eventKeys.disease(eventId));
    }
  }, [client, eventId]);

  return (
    <div className="relative">
      <Skeleton
        show={isLoading || !configs}
        className="box-content absolute z-10 grid items-center w-full h-full grid-cols-7 gap-3 p-2 bg-gray-100 -left-2 -top-2"
      >
        <span className="block col-span-3 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-3 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-3 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-2 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-3 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-3 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-2 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-2 shadow-sm h-9 show-skeleton"></span>
        <span className="block col-span-3 shadow-sm h-9 show-skeleton"></span>
      </Skeleton>
      <TabbedContainer
        tabs={tabs}
        activeId={activeId}
        setActiveId={setActiveId}
        addItemOptions={{
          onClick: onAddDisease,
          label: 'Additional Disease',
        }}
      >
        <DiseaseItem id={activeId} eventId={eventId} />
      </TabbedContainer>
    </div>
  );
}

DiseaseForm.save = async (eventId: string) => {
  const store = useRequestFormStore.getState();
  const primaryDiseaseKey = Object.keys(store.diseases).find(
    (key) => store.diseases[key].isPrimary
  ) as string;

  return await DiseaseItem.create(eventId, primaryDiseaseKey);
};
